import React, { useState } from "react";
import { FaCertificate, FaExternalLinkAlt, FaChevronDown } from "react-icons/fa";
import "./Certifications.css";

// ─── Certification data ───────────────────────────────────────────────────
const CERT_GROUPS = [
  {
    group: "Language & Communication",
    icon: "🌍",
    certs: [
      {
        id: "ielts",
        title: "IELTS Academic — Overall Band 6.5 (CEFR B2)",
        issuer: "British Council",
        date: "March 2026",
        certId: "TRF No. 09647379",
        details: "Listening 5.5 · Reading 6.5 · Writing 6.0 · Speaking 7.0",
        verifyUrl: null,
        pdfPath: "/ielts/TN00125505139-02-03-2026-ETRF.pdf",
        imagePath: null,
        skills: ["Academic English", "Cross-cultural Communication", "Research Writing"],
        relatedExp: "Post-exchange academic validation — applied for international graduate programmes",
      },
      {
        id: "korean-first-step",
        title: "First Step Korean",
        issuer: "Yonsei University (Coursera)",
        date: "March 2026",
        certId: "GC5OJ8LVFZQI",
        details: "Foundations of the Korean language — Hangul alphabet, pronunciation, basic vocabulary and sentence structure",
        verifyUrl: "https://coursera.org/verify/GC5OJ8LVFZQI",
        pdfPath: "/trainings/Coursera%20GC5OJ8LVFZQI.pdf",
        imagePath: null,
        skills: ["Korean Language", "Hangul", "Basic Korean Grammar", "Pronunciation"],
        relatedExp: "Personal language learning — Korean B1 proficiency",
      },
      {
        id: "korean-speak-1",
        title: "Learn to Speak Korean 1",
        issuer: "Yonsei University (Coursera)",
        date: "March 2026",
        certId: "S59XDBJLPCKQ",
        details: "Practical Korean speaking skills — conversational vocabulary, everyday expressions, and spoken communication",
        verifyUrl: "https://coursera.org/verify/S59XDBJLPCKQ",
        pdfPath: "/trainings/Coursera%20S59XDBJLPCKQ.pdf",
        imagePath: null,
        skills: ["Korean Speaking", "Korean Vocabulary", "Conversational Korean"],
        relatedExp: "Personal language learning — Korean B1 proficiency",
      },
      {
        id: "iaa",
        title: "IAA Accredited Course Completion",
        issuer: "International Advertising Association (IAA)",
        date: "January 2026",
        certId: "20714",
        details: "International advertising and marketing communications",
        verifyUrl: null,
        pdfPath: "/exchange-program/20714%20Ela%20Rabai.pdf",
        imagePath: null,
        skills: ["International Advertising", "Marketing Strategy", "Brand Communication"],
        relatedExp: "Erasmus+ Minor in International Communication",
      },
      {
        id: "eaca",
        title: "EACA Accreditation in Commercial Communications",
        issuer: "European Association of Communications Agencies (EACA)",
        date: "January 2026",
        certId: "1913659",
        details: "EU-level accreditation in commercial communications standards",
        verifyUrl: null,
        pdfPath: "/exchange-program/Rabai%2C%20Ela.pdf",
        imagePath: null,
        skills: ["Commercial Communications", "Digital Marketing", "European Marketing Standards"],
        relatedExp: "Erasmus+ Minor in International Communication",
      },
    ],
  },
  {
    group: "Machine Learning & AI",
    icon: "🤖",
    certs: [
      {
        id: "ibm-ai-engineering",
        title: "IBM AI Engineering Professional Certificate",
        issuer: "IBM (Coursera · 13 courses)",
        date: "April 2026",
        certId: "IN961HWZKM69",
        details: "Comprehensive 13-course professional certificate covering deep learning, neural networks (Keras, PyTorch, TensorFlow), LLMs, Generative AI, RAG with LangChain, and Hugging Face transformers",
        verifyUrl: "https://coursera.org/verify/professional-cert/IN961HWZKM69",
        pdfPath: "/trainings/Coursera%20IN961HWZKM69.pdf",
        imagePath: null,
        skills: ["Deep Learning", "PyTorch", "TensorFlow", "Keras", "LLMs", "LangChain", "Hugging Face", "RAG", "Generative AI", "NLP", "Transformers"],
        relatedExp: "Master's in Data Science · Rosa Luxemburg AI platform · ML research projects",
      },
      {
        id: "harvard-ml-ai",
        title: "Machine Learning and AI with Python",
        issuer: "Harvard Online (HarvardX · edX)",
        date: "March 2026",
        certId: "05ae7c1c99b34d398086bfbee3319467",
        details: "Comprehensive ML/AI curriculum — supervised, unsupervised learning, neural networks, and AI applications with Python",
        verifyUrl: null,
        pdfPath: "/trainings/HarvardX%20CS109xa%20Certificate%20_%20edX.pdf",
        imagePath: null,
        skills: ["Python", "ML Algorithms", "Neural Networks", "Data Science", "AI Applications"],
        relatedExp: "Master's in Data Science · Carbon Insights Data Scientist role",
      },
      {
        id: "harvard-ds-ml",
        title: "Data Science: Building Machine Learning Models",
        issuer: "Harvard Online (HarvardX · edX)",
        date: "February 2026",
        certId: "7e0c4c91d0d748ddbe596373409eeace",
        details: "Building, training, evaluating and deploying ML models — regression, classification, model selection and validation pipelines",
        verifyUrl: null,
        pdfPath: "/trainings/HarvardX%20PH125.8x%20Certificate%20_%20edX.pdf",
        imagePath: null,
        skills: ["ML Model Building", "Model Evaluation", "Scikit-Learn", "Data Pipelines"],
        relatedExp: "Master's projects: Breast Cancer Prediction, Water Analysis",
      },
      {
        id: "freecodecamp-ml",
        title: "Machine Learning with Python",
        issuer: "freeCodeCamp",
        date: "September 2024",
        certId: "fcc93cb0330-2dcc-4b83-84d8-64970e303a36-mlwp",
        details: "TensorFlow-based ML course covering neural networks, NLP, reinforcement learning and real-world projects",
        verifyUrl: "https://www.freecodecamp.org/certification/ela2002/machine-learning-with-python-v7",
        pdfPath: null,
        imagePath: "/trainings/machine%20learning%20certification.png",
        skills: ["TensorFlow", "Neural Networks", "NLP", "Reinforcement Learning"],
        relatedExp: "Carbon Insights internship · Academic ML projects",
      },
      {
        id: "ibm-ai",
        title: "Artificial Intelligence Fundamentals",
        issuer: "IBM SkillsBuild — Student Ambassador Program",
        date: "September 2024",
        certId: null,
        details: "AI foundations: machine learning, deep learning, computer vision, NLP, and responsible AI",
        verifyUrl: null,
        pdfPath: null,
        imagePath: "/trainings/DS%20foundations.jpg",
        skills: ["AI Fundamentals", "Deep Learning", "Responsible AI", "IBM Cloud"],
        relatedExp: "Academic AI projects · Current Rosa Luxemburg platform",
      },
    ],
  },
  {
    group: "AI Ethics & Governance",
    icon: "🛡️",
    certs: [
      {
        id: "ethical-ai-cambridge",
        title: "Ethical AI: AI Essentials for Everyone",
        issuer: "University of Cambridge (Coursera)",
        date: "April 2026",
        certId: "4Y4D8TUGI6MO",
        details: "Ethical dimensions of AI — bias, fairness, transparency, accountability, and the social impact of AI systems, from the University of Cambridge",
        verifyUrl: "https://coursera.org/verify/4Y4D8TUGI6MO",
        pdfPath: "/trainings/Coursera%204Y4D8TUGI6MO%202.pdf",
        imagePath: null,
        skills: ["AI Ethics", "Responsible AI", "AI Bias & Fairness", "AI Transparency", "Societal Impact of AI"],
        relatedExp: "AI Engineering certification · Rosa Luxemburg AI platform development",
      },
      {
        id: "ai-governance-oxford",
        title: "AI Governance",
        issuer: "Saïd Business School, University of Oxford (Coursera)",
        date: "March 2026",
        certId: "H2ZMQNSLFOBH",
        details: "AI governance frameworks, risk management, regulatory landscape, and responsible deployment of AI systems — from Oxford's Saïd Business School",
        verifyUrl: "https://coursera.org/verify/H2ZMQNSLFOBH",
        pdfPath: null,
        imagePath: "/trainings/H2ZMQNSLFOBH.jpeg",
        skills: ["AI Governance", "AI Risk Management", "AI Regulation", "Responsible AI Deployment"],
        relatedExp: "AI Ethics research · Rosa Luxemburg policy and AI work",
      },
      {
        id: "responsible-genai-michigan",
        title: "Responsible Generative AI Specialization",
        issuer: "University of Michigan (Coursera · 4 courses)",
        date: "April 2026",
        certId: "WXPRI7MIU7IP",
        details: "4-course specialization covering GenAI fundamentals, business and societal impact, governance and policy, emerging regulation, and the future of work with AI",
        verifyUrl: "https://coursera.org/verify/specialization/WXPRI7MIU7IP",
        pdfPath: "/trainings/Coursera%20WXPRI7MIU7IP.pdf",
        imagePath: null,
        skills: ["Responsible GenAI", "AI Policy", "AI & Society", "AI Regulation", "Future of Work"],
        relatedExp: "AI Engineering certification · Rosa Luxemburg AI strategy",
      },
    ],
  },
  {
    group: "Data Science & Analytics",
    icon: "📊",
    certs: [
      {
        id: "ds-foundations-ibm-london",
        title: "Data Science Foundations Specialization",
        issuer: "University of London + IBM (Coursera · 8 courses)",
        date: "April 2026",
        certId: "N0YO8DOOIP4N",
        details: "8-course specialization covering data science methodology, Python for DS, statistics, K-means clustering, ML, JupyterLab, data APIs, and a real-world capstone project predicting bicycle rentals",
        verifyUrl: "https://coursera.org/verify/specialization/N0YO8DOOIP4N",
        pdfPath: "/trainings/Coursera%20N0YO8DOOIP4N.pdf",
        imagePath: null,
        skills: ["Python", "Data Science Methodology", "K-Means Clustering", "Statistical Analysis", "JupyterLab", "Data APIs", "IBM Watson"],
        relatedExp: "Master's in Data Science · Breast Cancer Detection & Water Consumption projects",
      },
      {
        id: "freecodecamp-da",
        title: "Data Analysis with Python",
        issuer: "freeCodeCamp",
        date: "September 2024",
        certId: "fcc93cb0330-2dcc-4b83-84d8-64970e303a36-dawp",
        details: "Data wrangling, statistical analysis, NumPy, Pandas, Matplotlib, Seaborn, and real-world data projects",
        verifyUrl: "https://www.freecodecamp.org/certification/ela2002/data-analysis-with-python-v7",
        pdfPath: "/trainings/Data%20Analysis%20Certificate.pdf",
        imagePath: "/trainings/data%20analysis%20certification.png",
        skills: ["Python", "Pandas", "NumPy", "Matplotlib", "Seaborn", "Statistical Analysis"],
        relatedExp: "Carbon Insights · Global Water Consumption project",
      },
      {
        id: "great-learning-ds",
        title: "Introduction to Data Science",
        issuer: "Great Learning",
        date: "September 2024",
        certId: null,
        details: "Data science fundamentals: data types, statistics, Python for data science, and introductory ML",
        verifyUrl: null,
        pdfPath: null,
        imagePath: "/trainings/into%20to%20ds.jpg",
        skills: ["Data Science Foundations", "Statistics", "Python", "Data Visualisation"],
        relatedExp: "Entry into Master's in Data Science",
      },
      {
        id: "google-analytics",
        title: "Google Analytics Certification",
        issuer: "Google Digital Academy (Skillshop)",
        date: "September 2024",
        certId: "114670094",
        details: "Google Analytics 4 — tracking, reporting, audience analysis, conversion tracking",
        verifyUrl: null,
        pdfPath: null,
        imagePath: "/trainings/google%20analytics%20certification.png",
        skills: ["Google Analytics 4", "Digital Marketing Analytics", "Conversion Tracking"],
        relatedExp: "Erasmus+ Digital Media courses · IAA/EACA marketing certs",
      },
      {
        id: "great-learning-excel",
        title: "Data Analytics using Excel",
        issuer: "Great Learning",
        date: "September 2024",
        certId: null,
        details: "Excel for data analytics — pivot tables, VLOOKUP, data visualisation, dashboards",
        verifyUrl: null,
        pdfPath: null,
        imagePath: "/trainings/data%20analytics%20excel%20certification.jpg",
        skills: ["Excel", "Data Dashboards", "Pivot Tables", "Business Analytics"],
        relatedExp: "Market research at Carbon Insights",
      },
      {
        id: "google-gen-ai",
        title: "Introduction to Generative AI",
        issuer: "Google Cloud Skills Boost",
        date: "September 2024",
        certId: null,
        details: "Foundations of generative AI — LLMs, diffusion models, prompt engineering, responsible AI",
        verifyUrl: null,
        pdfPath: null,
        imagePath: null,
        skills: ["Generative AI", "LLMs", "Prompt Engineering", "Google Cloud AI"],
        relatedExp: "AI work at Rosa Luxemburg Stiftung",
      },
    ],
  },
  {
    group: "Web & Software Engineering",
    icon: "💻",
    certs: [
      {
        id: "carbon-internship-cert",
        title: "Certificate of Internship — Carbon Insights",
        issuer: "Carbon Insights (Vienna)",
        date: "September 2024",
        certId: null,
        details: "Official certificate for a data science and management consulting internship at Carbon Insights, Vienna — renewable energy market analysis and predictive modelling.",
        verifyUrl: null,
        pdfPath: "/trainings/carbon%20insights%20inetnship%20cert.pdf",
        imagePath: null,
        skills: ["Data Science", "Market Research", "Predictive Modelling", "Consulting"],
        relatedExp: "Carbon Insights Management Consultant & Data Scientist — Aug–Sep 2024",
      },
      {
        id: "saylor-se",
        title: "Software Engineering",
        issuer: "Saylor University",
        date: "September 2024",
        certId: "8890694181ER",
        details: "Software engineering lifecycle — requirements, design patterns, testing, agile methodologies, and system architecture",
        verifyUrl: null,
        pdfPath: "/trainings/software%20engineering%20certificate.pdf",
        imagePath: null,
        skills: ["Software Architecture", "Design Patterns", "Agile / Scrum", "Testing"],
        relatedExp: "Highscale · CNI · CIM internships",
      },
      {
        id: "cni-internship-cert",
        title: "Certificate of Internship — CNI",
        issuer: "Centre National de l'Informatique",
        date: "July 2023",
        certId: null,
        details: "Official completion certificate for a web development internship at the Tunisian national IT centre — built an Angular + Spring Boot asset management system.",
        verifyUrl: null,
        pdfPath: null,
        imagePath: "/internships/cni%20internship%20certif.jpeg",
        skills: ["Angular", "Spring Boot", "PostgreSQL", "Enterprise Java"],
        relatedExp: "CNI Web Developer Internship — Jun–Jul 2023",
      },
      {
        id: "internship-cert",
        title: "Certificate of Internship",
        issuer: "Highscale",
        date: "May 2024",
        certId: null,
        details: "Official completion certificate for a 5-month software engineering internship building the Powerback web and mobile platform at Highscale.",
        verifyUrl: null,
        pdfPath: "/internships/Certificate%20of%20Internship_sk.pdf",
        imagePath: "/internships/highscale%20internship%20certif.jpeg",
        skills: ["React.js", "React Native", "Firebase", "Full-Stack Development"],
        relatedExp: "Highscale Software Engineer Internship — Jan–May 2024",
      },
    ],
  },
  {
    group: "Entrepreneurship & Leadership",
    icon: "🚀",
    certs: [
      {
        id: "hult-prize-winner",
        title: "Hult Prize at ISTIC — 2nd Runner-Up",
        issuer: "Hult Prize Foundation · ISTIC On-Campus Round",
        date: "2023",
        certId: null,
        details: "Recognised as 2nd Runner-Up at the ISTIC on-campus round of the Hult Prize — the world's largest student social entrepreneurship competition.",
        verifyUrl: null,
        pdfPath: null,
        imagePath: "/enterpreneurship/hult%20prize%20winning%20certif.jpeg",
        skills: ["Social Entrepreneurship", "Pitch Presentation", "Business Modelling", "Team Leadership"],
        relatedExp: "Hult Prize at ISTIC — Associative Experience",
      },
      {
        id: "hult-prize-committee",
        title: "Hult Prize Organising Committee Member",
        issuer: "Hult Prize Foundation · ISTIC",
        date: "2023",
        certId: null,
        details: "Recognised for service as a member of the Judges & Experts Committee at the Hult Prize ISTIC campus round — evaluating pitches and coordinating the event.",
        verifyUrl: null,
        pdfPath: null,
        imagePath: "/enterpreneurship/hult%20prize%20organizing%20commetee%20member.jpeg",
        skills: ["Event Management", "Jury Evaluation", "Leadership", "Entrepreneurship Mentoring"],
        relatedExp: "Hult Prize Organising Committee — Associative Experience",
      },
    ],
  },
];

// ─── CertMedia ─────────────────────────────────────────────────────────────
const CertMedia = ({ cert }) => {
  if (cert.imagePath) {
    return (
      <div className="cert-media">
        <img
          src={cert.imagePath}
          alt={cert.title}
          className="cert-img"
          draggable="false"
          onContextMenu={(e) => e.preventDefault()}
          onError={(e) => { e.target.style.display = "none"; }}
        />
      </div>
    );
  }
  if (cert.pdfPath) {
    return (
      <div className="cert-pdf-embed-wrap">
        <embed
          src={cert.pdfPath}
          type="application/pdf"
          width="100%"
          height="380"
          style={{ display: "block", borderRadius: 8 }}
        />
      </div>
    );
  }
  return null;
};

// ─── CertCard ─────────────────────────────────────────────────────────────
const CertCard = ({ cert }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={`cert-card ${open ? "cert-card--open" : ""}`}>
      <button className="cert-card__header" onClick={() => setOpen(!open)}>
        <div className="cert-card__header-left">
          <FaCertificate className="cert-card__icon" />
          <div>
            <div className="cert-card__title">{cert.title}</div>
            <div className="cert-card__meta">
              <span className="cert-card__issuer">{cert.issuer}</span>
              <span className="cert-card__date">· {cert.date}</span>
            </div>
          </div>
        </div>
        <span className="cert-card__chevron">
          <FaChevronDown />
        </span>
      </button>

      {open && (
        <div className="cert-card__body">
          {/* Real cert image or PDF link */}
          <CertMedia cert={cert} />

          <p className="cert-details">{cert.details}</p>

          {cert.certId && (
            <div className="cert-id">
              <span>🔑 Credential ID:</span> <code>{cert.certId}</code>
            </div>
          )}

          <div className="cert-section">
            <div className="cert-section__label">Skills Acquired</div>
            <div className="cert-tags">
              {cert.skills.map((s, i) => (
                <span key={i} className="cert-tag">{s}</span>
              ))}
            </div>
          </div>

          <div className="cert-section">
            <div className="cert-section__label">Related Experience</div>
            <p className="cert-related">{cert.relatedExp}</p>
          </div>

          {cert.verifyUrl && (
            <a
              href={cert.verifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="cert-verify-btn"
            >
              <FaExternalLinkAlt /> Verify Credential
            </a>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Certifications ───────────────────────────────────────────────────────
const Certifications = () => {
  const total = CERT_GROUPS.reduce((sum, g) => sum + g.certs.length, 0);

  return (
    <div className="certifications-section">
      <h2 className="section-title">Certifications</h2>
      <p className="cert-intro">
        {total} certifications from Harvard, Cambridge, Oxford, Michigan, IBM, Google, Yonsei and more —
        spanning AI engineering, ethics &amp; governance, data science, software engineering, and languages.
      </p>

      {CERT_GROUPS.map((group) => (
        <div key={group.group} className="cert-group">
          <div className="cert-group__header">
            <span>{group.icon}</span> {group.group}
            <span className="cert-group__count">{group.certs.length}</span>
          </div>
          {group.certs.map((cert) => (
            <CertCard key={cert.id} cert={cert} />
          ))}
        </div>
      ))}
    </div>
  );
};

export default Certifications;
